import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { message, Tooltip } from "antd";
import { CheckCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { create_hike_user } from "../../../store/Admin/UserManagement/UserManagementSlice";
import CreateCustomerModal from "./CreateCustomerModal";
import ViewButton from "../Common/ViewButton";
import UserListPagination from "./UserListPagination";
import "../../../pages/Admin/Queries.css";

/** `branch_name` comes as an array (e.g. ["NEW DELHI"]); show every entry comma-separated. */
export function formatBranches(branches) {
  const list = (Array.isArray(branches) ? branches : [branches])
    .map((b) => (typeof b === "string" ? b.trim() : ""))
    .filter(Boolean);
  return list.length ? list.join(", ") : "—";
}

/**
 * KAM user list — name, organization, KAM, customer type, branches, styled like the Queries table
 * (amber header, striped rows, status chips, custom pagination).
 * Pagination is server-side: `data` is the current page only and `total` is
 * the backend's `pagination.total`.
 */
function KamUserKycTable({ data = [], total = 0, currentPage, pageSize, loading, kamName, onPageChange, onPageSizeChange, onView }) {
  const dispatch = useDispatch();

  // User whose create-customer modal is open (null = closed).
  const [modalUser, setModalUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Row ids whose customer was created successfully — button swaps for a tick.
  const [createdIds, setCreatedIds] = useState(() => new Set());

  const handleCreateCustomer = (values) => {
    const userId = modalUser?._id;
    if (!userId) return;
    setSubmitting(true);
    dispatch(create_hike_user({ user_id: userId, ...values }))
      .then((res) => {
        const status = res?.payload?.status;
        if (status >= 200 && status < 300) {
          setCreatedIds((prev) => new Set(prev).add(userId));
          message.success(res?.payload?.data?.message || "User created");
          setModalUser(null);
        } else {
          message.error(res?.payload?.data?.message || "Failed to create user");
        }
      })
      .catch(() => message.error("Failed to create user"))
      .finally(() => setSubmitting(false));
  };

  const fromIdx = (currentPage - 1) * pageSize;

  return (
    <div className="bq-wrap" style={{ position: "relative" }}>
      {loading && (
        <div className="bq-loading-overlay">
          <div className="bq-loading-spinner" />
        </div>
      )}
      <div className="tableResponsiveASAP">
        <table className="bq-table">
          <thead>
            <tr>
              <th style={{ width: 80 }}>S. No.</th>
              <th style={{ width: 200 }}>User Full Name</th>
              <th style={{ width: 240 }}>Organization Name</th>
              <th style={{ width: 200 }}>Key Account Manager</th>
              <th style={{ width: 140 }}>Customer Type</th>
              <th style={{ width: 200 }}>Branch</th>
              <th style={{ width: 160 }}>Platform Name</th>
              <th style={{ width: 200 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.length === 0 ? (
              <tr>
                <td colSpan={8} className="bq-empty">No users found.</td>
              </tr>
            ) : (
              data.map((record, index) => {
                return (
                  <tr key={record?._id ?? fromIdx + index}>
                    <td>{fromIdx + index + 1}</td>
                    <td>{record?.full_name || "—"}</td>
                    <td>{record?.company_name || "—"}</td>
                    <td>{record?.key_account_manager?.full_name || kamName || "—"}</td>
                    <td>{record?.customer_type || "—"}</td>
                    <td>{formatBranches(record?.branch_name)}</td>
                    <td>{record?.platform_name || "—"}</td>
                    <td>
                      <div className="bq-actions">
                        {onView && <ViewButton handleClick={onView} data={record} />}
                        {Number(record?.is_hike_user_created) === 1 || createdIds.has(record?._id) ? (
                          <Tooltip title="Customer created">
                            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 22, cursor: "pointer" }} />
                          </Tooltip>
                        ) : Number(record?.kyc_verification_status) !== 1 ? (
                          <Tooltip title="KYC for the user must be done">
                            <InfoCircleOutlined
                              aria-label="KYC pending"
                              style={{ color: "#faad14", fontSize: 20, cursor: "pointer" }}
                            />
                          </Tooltip>
                        ) : (
                          <button
                            className="bq-manual-quote-btn"
                            disabled={!record?._id || (submitting && modalUser?._id === record?._id)}
                            onClick={() => setModalUser(record)}
                          >
                            Create User
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <UserListPagination
        total={total}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <CreateCustomerModal
        open={!!modalUser}
        user={modalUser}
        submitting={submitting}
        onCancel={() => !submitting && setModalUser(null)}
        onSubmit={handleCreateCustomer}
      />
    </div>
  );
}

export default KamUserKycTable;
